import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";
import { Link } from "react-router";

const Breadcrumb = ({ ...props }) => <nav aria-label="breadcrumb" {...props} />;
Breadcrumb.displayName = "Breadcrumb";

const BreadcrumbList = ({
    className,
    ...props
}: React.ComponentProps<"ol">) => (
    <ol
        className={cn(
            "text-small leading-small text-subtle sm:gap-narrow flex flex-wrap items-center gap-1.5 font-bold break-words",
            className,
        )}
        {...props}
    />
);
BreadcrumbList.displayName = "BreadcrumbList";

const BreadcrumbItem = ({
    className,
    ...props
}: React.ComponentProps<"li">) => (
    <li
        className={cn("text-default-color max-w-52 truncate", className)}
        {...props}
    />
);
BreadcrumbItem.displayName = "BreadcrumbItem";

const BreadcrumbLink = ({
    asChild,
    className,
    ...props
}: React.ComponentProps<typeof Link> & { asChild?: boolean }) => {
    const Comp = asChild ? Slot : Link;

    return (
        <Comp
            className={cn(
                "rounded-narrow p-narrower text-default-color hover:bg-neutral-subtlest-hover active:bg-neutral-subtlest-press ring-offset-0 transition-colors",
                className,
            )}
            {...props}
        />
    );
};
BreadcrumbLink.displayName = "BreadcrumbLink";

const BreadcrumbPage = ({
    className,
    ...props
}: React.ComponentProps<"span">) => (
    <span
        role="link"
        aria-disabled="true"
        aria-current="page"
        className={cn("font-default", className)}
        {...props}
    />
);
BreadcrumbPage.displayName = "BreadcrumbPage";

const BreadcrumbSeparator = ({
    children,
    className,
    ...props
}: React.ComponentProps<"li">) => (
    <li
        role="presentation"
        aria-hidden="true"
        className={cn("px-narrow [&>svg]:h-3.5 [&>svg]:w-3.5", className)}
        {...props}
    >
        {children ?? "/"}
    </li>
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

const BreadcrumbEllipsis = ({
    className,
    ...props
}: React.ComponentProps<"span">) => (
    <span
        role="presentation"
        aria-hidden="true"
        className={cn("flex h-9 w-9 items-center justify-center", className)}
        {...props}
    >
        <MoreHorizontal className="h-4 w-4" />
        <span className="sr-only">More</span>
    </span>
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";

export {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbEllipsis,
};
